import {useState, useCallback} from 'react';
import {MultipleFetchOptions, MultipleFetchResult, Request} from './types';

const DEFAULT_SENDER = 'Hakupalvelu';

const DEFAULT_FETCH_OPTIONS = {
  // Explicitly set same-origin for credentials so that auth cookie works on older browsers
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json'
  }
};

export const shouldSetResultAsJson = (
  initialRequest?: Request[],
  requestOpts?: Request[]
): boolean => {
  if (requestOpts && requestOpts[0]?.json !== undefined) {
    return requestOpts[0]?.json !== false;
  } else {
    return initialRequest ? initialRequest[0]?.json !== false : false;
  }
};

//Allows get requesting a dynamic number of resources at once, very barebones, intended for Kopa codeset queries
const useMultipleFetch = <T>({
  sender = DEFAULT_SENDER,
  request
}: Partial<MultipleFetchOptions>) => {
  const [data, setData] = useState<MultipleFetchResult<T>[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Record<string, unknown> | null>(null);
  const [statusCodes, setStatusCodes] = useState<number[]>();

  const fetchData = useCallback(
    async (requestOpts: Request[]) => {
      const urls =
        requestOpts?.map(r => r?.url || '') ?? request?.map(r => r?.url || '');
      const method =
        requestOpts?.find(r => r?.method || '')?.method ||
        request?.find(r => r?.method || '')?.method ||
        'GET';
      const headers =
        requestOpts?.find(r => r?.headers)?.headers ??
        request?.find(r => r?.headers)?.headers ??
        DEFAULT_FETCH_OPTIONS.headers;
      const resultsAsJson = shouldSetResultAsJson(request, requestOpts);
      const credentials =
        requestOpts?.find(r => r?.credentials || '')?.credentials ||
        request?.find(r => r?.credentials || '')?.credentials ||
        DEFAULT_FETCH_OPTIONS.credentials;
      setIsLoading(true);
      setError(null);
      const opts = {
        ...DEFAULT_FETCH_OPTIONS,
        method,
        headers: {...DEFAULT_FETCH_OPTIONS.headers, ...headers},
        credentials
      } as RequestInit;

      let result = null;
      try {
        const resp = await getAllResponses(urls, opts);
        const statusCodes = resp.map(r => r?.status);
        setStatusCodes(statusCodes);
        if (resp.length === resp.filter(r => r.ok).length && resultsAsJson) {
          try {
            result = await getAllResults(resp);
          } catch (err) {
            throw new Error(JSON.stringify(err));
          }
        }
      } catch (error) {
        const err = error as Record<string, unknown>;
        err[sender] = sender;
        setError(err);
      } finally {
        setData(result);
        setIsLoading(false);
      }
    },
    [request, sender, setData, setIsLoading, setStatusCodes]
  );

  const getAllResponses = (urls: string[], opts: RequestInit) => {
    return Promise.all(urls.map(async url => await fetch(url, opts)));
  };

  const getAllResults = (resp: Response[]) => {
    const promises = resp.map(async r => {
      const json = await r.json();
      return {
        result: json,
        entityName: r.url
      };
    });
    return Promise.all(promises);
  };

  const doRequest = useCallback(
    (requestOpts: Request[]) => {
      return fetchData(requestOpts);
    },
    [fetchData]
  );

  return {
    isLoading,
    data,
    error,
    statusCodes,
    doRequest
  };
};

export default useMultipleFetch;
